import React, { useState } from "react";
import axios from "axios";
import { Download, FileJson, Mail, Loader2, Check } from "lucide-react";
import { API } from "@/contexts/AuthContext";

/**
 * Export Actions — quiet row of three actions.
 *
 *   • Download JSON  — GET /intake/sessions/:id/export.json
 *   • Download PDF   — GET /intake/sessions/:id/export.pdf
 *   • Email          — POST /intake/sessions/:id/push-to-garage-hive
 */
export default function ExportActions({ sessionId, vehicleReg }) {
  const [emailStatus, setEmailStatus] = useState(null); // null | "sending" | "sent" | "failed"
  const [emailMessage, setEmailMessage] = useState(null);

  const fileNameBase = `intake_${vehicleReg || "session"}`;

  const downloadBlob = async (path, filename) => {
    try {
      const res = await axios.get(`${API}/intake/sessions/${sessionId}/${path}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.detail || "Download failed");
    }
  };

  const emailToWorkshop = async () => {
    const to = window.prompt(
      "Email destination (leave blank to use the configured workshop inbox):",
      ""
    );
    if (to === null) return;
    setEmailStatus("sending");
    setEmailMessage(null);
    try {
      const res = await axios.post(
        `${API}/intake/sessions/${sessionId}/push-to-garage-hive`,
        to ? { to_email: to.trim() } : {}
      );
      const s = res.data?.status;
      if (s === "sent") {
        setEmailStatus("sent");
        setEmailMessage(`Sent to ${res.data.to_email}`);
      } else if (s === "skipped") {
        setEmailStatus("sent");
        setEmailMessage(res.data.note || "Email skipped (no key configured)");
      } else {
        setEmailStatus("failed");
        setEmailMessage(res.data?.error || s);
      }
      setTimeout(() => setEmailStatus(null), 5000);
    } catch (e) {
      setEmailStatus("failed");
      setEmailMessage(e?.response?.data?.detail || "Email failed");
      setTimeout(() => setEmailStatus(null), 5000);
    }
  };

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white"
      data-testid="export-actions"
    >
      <header className="px-4 pt-3 pb-1.5 border-b border-slate-100">
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">
          Push to Garage Hive
        </p>
      </header>
      <div className="px-4 py-3 grid grid-cols-3 gap-1.5">
        <ExportButton
          icon={<FileJson className="w-3.5 h-3.5" />}
          label="JSON"
          onClick={() => downloadBlob("export.json", `${fileNameBase}.json`)}
          testId="export-json-btn"
        />
        <ExportButton
          icon={<Download className="w-3.5 h-3.5" />}
          label="PDF"
          onClick={() => downloadBlob("export.pdf", `${fileNameBase}.pdf`)}
          testId="export-pdf-btn"
        />
        <ExportButton
          icon={
            emailStatus === "sending" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : emailStatus === "sent" ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <Mail className="w-3.5 h-3.5" />
            )
          }
          label={emailStatus === "sent" ? "Sent" : "Email"}
          onClick={emailToWorkshop}
          disabled={emailStatus === "sending"}
          tone={emailStatus === "sent" ? "teal" : "default"}
          testId="export-email-btn"
        />
      </div>
      {emailMessage && (
        <p className="px-4 pb-3 text-[11px] text-slate-500 italic">{emailMessage}</p>
      )}
    </section>
  );
}

function ExportButton({ icon, label, onClick, disabled, tone, testId }) {
  const cls =
    tone === "teal"
      ? "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100"
      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-1.5 text-[12px] font-medium border rounded px-2 py-1.5 transition-all ${cls} disabled:opacity-50`}
      data-testid={testId}
    >
      {icon}
      {label}
    </button>
  );
}
